// src/pages/About.jsx
import { Fragment, useMemo, useRef } from 'react';
import { WebLayout } from 'components/Layout';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Disclosure, Transition } from '@headlessui/react';
import {
  Tool,
  Award,
  Shield,
  MapPin,
  Truck,
  Clock,
  Heart,
  Users,
  Star,
  Instagram,
  MessageCircle,
  Send,
  Check,
  ChevronDown,
  ArrowRight,
} from 'react-feather';
import workshopImg from 'assets/workshop.jpeg';

const ORDER_LINKS = {
  whatsappNumber: '256782692271', // no plus sign
  instagram: 'https://instagram.com/gambit_games_ug',
  telegram: 'https://t.me/gambit_games_ug',
};

export function AboutUs() {
  // Parallax hero
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0.85]);

  const values = useMemo(
    () => [
      {
        icon: <Tool size={18} />,
        title: 'Craftsmanship',
        text: 'Precision joinery, smooth finishing, and durable coatings for everyday play.',
      },
      {
        icon: <Tool size={18} />,
        title: 'Local Woods',
        text: 'We prioritise Mugavu and other regional hardwoods, responsibly sourced.',
      },
      {
        icon: <Shield size={18} />,
        title: 'Built to Last',
        text: 'Solid construction, felted bottoms, and hardwearing surfaces.',
      },
      {
        icon: <Heart size={18} />,
        title: 'Play First',
        text: 'We obsess over feel—piece weight, glide, click, and table presence.',
      },
    ],
    []
  );

  const process = useMemo(
    () => [
      {
        step: '01',
        title: 'Design & Layout',
        text: 'We size the board, choose inlays/engraving, and plan piece proportions.',
      },
      {
        step: '02',
        title: 'Select & Prep Wood',
        text: 'Boards are matched for grain and stability; parts milled and squared.',
      },
      {
        step: '03',
        title: 'Cut • Carve • Inlay',
        text: 'We carve cups, route lines, and set inlays for contrast and longevity.',
      },
      {
        step: '04',
        title: 'Sand & Finish',
        text: 'Progressive sanding for a buttery feel, then oil/varnish in thin coats.',
      },
      {
        step: '05',
        title: 'Inspect & Pack',
        text: 'Fit, felt, and finish checks; secure packaging for Kampala delivery.',
      },
    ],
    []
  );

  const faqs = useMemo(
    () => [
      {
        q: 'Do you deliver outside Kampala?',
        a: 'Currently we deliver within Kampala. For special requests beyond Kampala, chat with us and we’ll advise on options.',
      },
      {
        q: 'Can I request a fully custom game?',
        a: 'Yes. From custom Ludo engravings to brand-new mechanics—start on the Custom page and we’ll guide you.',
      },
      {
        q: 'Which woods do you recommend?',
        a: 'Mugavu (Cordia africana) and Mahogany for premium builds; Pine for budget and kids sets; mixed hardwood inlays for showpieces.',
      },
      {
        q: 'How long does production take?',
        a: 'Standard items are often ready in 2–5 days. Complex custom work can take 5–10+ days depending on details and finishing.',
      },
      {
        q: 'Do you handle corporate/bulk orders?',
        a: 'Absolutely. We prepare curated bundles with logos and gift packaging—great for events and client gifts.',
      },
    ],
    []
  );

  return (
    <WebLayout>
      {/* HERO */}
      <section ref={heroRef} className="relative isolate overflow-hidden bg-gray-950">
        <motion.div style={{ y, opacity }} className="pointer-events-none absolute inset-0">
          <img
            src="/assets/about_hero.jpg"
            alt=""
            className="h-full w-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-gray-950/60 via-gray-950/60 to-gray-950" />
        </motion.div>

        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#12A2BF]/30 bg-[#12A2BF]/10 px-3 py-1 text-xs font-medium text-[#12A2BF]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#12A2BF]" />
              Made in Uganda • Najjera • Kampala delivery
            </div>
            <h1 className="mt-4 text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              About Gambit Games
            </h1>
            <p className="mt-3 max-w-xl text-sm text-gray-300">
              We craft wooden board games that feel great to play and look beautiful on the
              table—classic, cultural, travel, party, and fully custom builds.
            </p>

            <div className="mt-5 flex flex-wrap items-center gap-2">
              <Link
                to="/"
                className="inline-flex items-center gap-2 rounded-md bg-[#12A2BF] px-4 py-2 text-sm font-semibold text-black hover:brightness-110"
              >
                Shop Games <ArrowRight size={16} />
              </Link>
              <Link
                to="/custom"
                className="inline-flex items-center gap-2 rounded-md border border-gray-700 px-4 py-2 text-sm text-gray-200 hover:bg-gray-800"
              >
                Start a Custom Order
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* STORY */}
      <section className="bg-gray-950">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="grid items-center gap-8 lg:grid-cols-2">
            <div className="order-2 lg:order-1">
              <h2 className="text-xl font-semibold text-white">Our Story</h2>
              <p className="mt-3 text-sm leading-6 text-gray-300">
                Gambit Games began with Kampala game nights—friends, family, and a hunt for boards
                that felt as good as they played. We paired woodworking with gameplay love, and the
                result is a studio in Najjera building boards with character and longevity.
              </p>
              <p className="mt-3 text-sm leading-6 text-gray-300">
                From Omweso/Mancala to Chess and Ludo, we build with respect for tradition and an
                eye for modern usability: weight, glide, finish, storage, and effortless setup.
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center gap-2 rounded-full border border-[#12A2BF]/30 bg-[#12A2BF]/10 px-3 py-1 text-xs font-medium text-[#12A2BF]">
                  <Star size={14} />
                  Community-loved
                </span>
                <span className="inline-flex items-center gap-2 rounded-full border border-[#12A2BF]/30 bg-[#12A2BF]/10 px-3 py-1 text-xs font-medium text-[#12A2BF]">
                  <Tool size={14} />
                  Handcrafted
                </span>
                <span className="inline-flex items-center gap-2 rounded-full border border-[#12A2BF]/30 bg-[#12A2BF]/10 px-3 py-1 text-xs font-medium text-[#12A2BF]">
                  <MapPin size={14} />
                  Made in Najjera
                </span>
              </div>
            </div>

            <div className="order-1 overflow-hidden rounded-2xl border border-gray-800 bg-gray-900 lg:order-2">
              <img
                src={workshopImg}
                alt="Gambit Games workshop in Najjera"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="bg-gray-950">
        <div className="mx-auto max-w-7xl px-4 pb-6 sm:px-6 lg:px-8">
          <div className="rounded-2xl border border-gray-800 bg-gray-900 p-6">
            <h2 className="text-xl font-semibold text-white">What We Value</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {values.map((v) => (
                <div key={v.title} className="rounded-xl border border-gray-800 bg-gray-950 p-4">
                  <div className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#12A2BF]/10 text-[#12A2BF]">
                    {v.icon}
                  </div>
                  <h3 className="mt-3 text-sm font-semibold text-white">{v.title}</h3>
                  <p className="mt-1 text-xs text-gray-400">{v.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CRAFT PROCESS */}
      <section className="bg-gray-950">
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
          <div className="grid gap-8 lg:grid-cols-2">
            <div className="overflow-hidden rounded-2xl border border-gray-800 bg-gray-900">
              <img
                src="/assets/about/workshop_2.jpg"
                alt="Wood selection and prep"
                className="h-full w-full object-cover"
              />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">How We Build</h2>
              <p className="mt-2 text-sm text-gray-300">
                Every board is individually prepared, inspected, and finished for a long life on
                your table.
              </p>
              <ol className="mt-4 space-y-3">
                {process.map((p) => (
                  <li key={p.step} className="rounded-xl border border-gray-800 bg-gray-900 p-4">
                    <div className="flex items-center gap-3">
                      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#12A2BF]/10 text-xs font-bold text-[#12A2BF]">
                        {p.step}
                      </span>
                      <h3 className="text-sm font-semibold text-white">{p.title}</h3>
                    </div>
                    <p className="mt-1 pl-10 text-xs text-gray-400">{p.text}</p>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          {/* Materials + Service row */}
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-xl border border-gray-800 bg-gray-900 p-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-white">
                <Tool size={16} /> Woods We Love
              </div>
              <ul className="mt-2 list-disc pl-6 text-xs text-gray-400">
                <li>Mugavu (Cordia africana)</li>
                <li>Mahogany</li>
                <li>Mixed hardwood inlay</li>
                <li>Pine (budget/kids)</li>
              </ul>
            </div>
            <div className="rounded-xl border border-gray-800 bg-gray-900 p-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-white">
                <Truck size={16} /> Delivery
              </div>
              <p className="mt-2 text-xs text-gray-400">
                We deliver within Kampala; packaging is secure and compact.
              </p>
            </div>
            <div className="rounded-xl border border-gray-800 bg-gray-900 p-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-white">
                <Clock size={16} /> Lead Times
              </div>
              <p className="mt-2 text-xs text-gray-400">
                Ready items: 1–3 days. Customs: 3–10 days, depending on complexity.
              </p>
            </div>
            <div className="rounded-xl border border-gray-800 bg-gray-900 p-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-white">
                <Award size={16} /> Guarantee
              </div>
              <p className="mt-2 text-xs text-gray-400">
                If something’s off on arrival, we’ll make it right—just message us.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-gray-950">
        <div className="mx-auto max-w-7xl px-4 pb-10 sm:px-6 lg:px-8">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Boards Crafted', value: '500+' },
              { label: 'Custom Projects', value: '120+' },
              { label: 'Avg. Lead Time', value: '3–7 days' },
              { label: 'Customer Rating', value: '4.9/5' },
            ].map((s) => (
              <div
                key={s.label}
                className="rounded-xl border border-gray-800 bg-gray-900 p-5 text-center"
              >
                <div className="text-2xl font-extrabold text-white">{s.value}</div>
                <div className="mt-1 text-xs text-gray-400">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-gray-950">
        <div className="mx-auto max-w-7xl px-4 pb-10 sm:px-6 lg:px-8">
          <div className="rounded-2xl border border-gray-800 bg-gray-900 p-6">
            <h2 className="text-xl font-semibold text-white">What Players Say</h2>
            <div className="mt-4 grid gap-4 lg:grid-cols-3">
              {[
                {
                  text: 'The Omweso board is a work of art—smooth cups and the perfect seed flow.',
                  name: 'Michael, Ntinda',
                },
                {
                  text: 'Our custom Ludo with family name was the hit of the holidays!',
                  name: 'Sarah, Muyenga',
                },
                {
                  text: 'Mahogany chess set feels premium—great weight and finish.',
                  name: 'Ivan, Kira',
                },
              ].map((t) => (
                <blockquote
                  key={t.name}
                  className="rounded-xl border border-gray-800 bg-gray-950 p-4"
                >
                  <div className="flex items-center gap-2 text-amber-300">
                    <Star size={14} />
                    <Star size={14} />
                    <Star size={14} />
                    <Star size={14} />
                    <Star size={14} />
                  </div>
                  <p className="mt-2 text-sm text-gray-200">“{t.text}”</p>
                  <cite className="mt-2 block text-xs text-gray-400">— {t.name}</cite>
                </blockquote>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-gray-950">
        <div className="mx-auto max-w-7xl px-4 pb-12 sm:px-6 lg:px-8">
          <div className="rounded-2xl border border-gray-800 bg-gray-900 p-6">
            <h2 className="text-xl font-semibold text-white">FAQs</h2>
            <div className="mt-2 divide-y divide-gray-800">
              {faqs.map((item, idx) => (
                <Disclosure as="div" key={idx} className="py-3">
                  {({ open }) => (
                    <>
                      <Disclosure.Button className="flex w-full items-center justify-between py-2 text-left text-sm text-gray-200 hover:text-white">
                        <span>{item.q}</span>
                        <ChevronDown
                          className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`}
                        />
                      </Disclosure.Button>
                      <Disclosure.Panel className="pr-6 text-sm text-gray-400">
                        {item.a}
                      </Disclosure.Panel>
                    </>
                  )}
                </Disclosure>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT / CTA STRIP */}
      <section className="bg-gray-950">
        <div className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8">
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="rounded-2xl border border-gray-800 bg-gray-900 p-6">
              <h3 className="text-lg font-semibold text-white">Visit & Contact</h3>
              <ul className="mt-3 space-y-2 text-sm text-gray-300">
                <li className="flex items-center gap-2">
                  <MapPin size={16} className="text-[#12A2BF]" /> Najjera, Kampala, Uganda
                </li>
                <li className="flex items-center gap-2">
                  <Truck size={16} className="text-[#12A2BF]" /> We deliver within Kampala
                </li>
              </ul>
              <div className="mt-4 grid gap-2 sm:grid-cols-3">
                <a
                  href={`https://wa.me/${ORDER_LINKS.whatsappNumber}?text=${encodeURIComponent(
                    'Hi Gambit Games! I have a question about your boards.'
                  )}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-[#12A2BF] px-3 py-2 text-sm font-semibold text-black hover:brightness-110"
                >
                  <MessageCircle size={16} />
                  WhatsApp
                </a>
                <a
                  href={ORDER_LINKS.instagram}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-pink-500/90 px-3 py-2 text-sm font-semibold text-white hover:brightness-110"
                >
                  <Instagram size={16} />
                  Instagram
                </a>
                <a
                  href={ORDER_LINKS.telegram}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-sky-500 px-3 py-2 text-sm font-semibold text-white hover:brightness-110"
                >
                  <Send size={16} />
                  Telegram
                </a>
              </div>
            </div>

            <div className="rounded-2xl border border-gray-800 bg-gray-900 p-6">
              <h3 className="text-lg font-semibold text-white">Ready to Play?</h3>
              <p className="mt-2 text-sm text-gray-300">
                Explore the Shop for ready boards, or design your own on the Custom page. We’ll
                craft it in Najjera and deliver within Kampala.
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 rounded-md bg-[#12A2BF] px-4 py-2 text-sm font-semibold text-black hover:brightness-110"
                >
                  Shop Now <ArrowRight size={16} />
                </Link>
                <Link
                  to="/custom"
                  className="inline-flex items-center gap-2 rounded-md border border-gray-700 px-4 py-2 text-sm text-gray-200 hover:bg-gray-800"
                >
                  Start a Custom Order
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </WebLayout>
  );
}
